// § 5 · Settings
// "Configure everything operational that doesn't fit elsewhere."
//
// Collapsible cards (default closed) so the section stays scannable.
// Order is operator-action frequency: Identity (rarely) → Storage →
// Security → Endpoints → Field aliases → Operations → Danger zone.

import { useState } from 'react';
import type { Source } from '@datahub/shared';
import { patchSource, rotateHmacSecret } from '../../api';
import { useToast } from '../Toast';
import { SectionShell } from './SectionShell';
import TagsEditor from '../TagsEditor';

interface Props {
  source: Source;
  onSourceChange: (s: Source) => void;
}

export function SettingsCardsV2({ source, onSourceChange }: Props): React.ReactElement {
  const cards: Array<{ id: string; title: string; render: () => React.ReactElement }> = [
    { id: 'identity', title: 'Identity', render: () => <IdentityCard source={source} onSourceChange={onSourceChange} /> },
    { id: 'storage', title: 'Storage & retention', render: () => <StorageCard source={source} onSourceChange={onSourceChange} /> },
    { id: 'security', title: 'Security (HMAC)', render: () => <SecurityCard source={source} onSourceChange={onSourceChange} /> },
    { id: 'endpoints', title: 'Endpoints & slug', render: () => <EndpointsCard source={source} /> },
    { id: 'aliases', title: 'Field aliases', render: () => <FieldAliasesCard source={source} /> },
    { id: 'ops', title: 'Operations', render: () => <OperationsCard source={source} /> },
    { id: 'danger', title: 'Danger zone', render: () => <DangerCard source={source} /> },
  ];
  return (
    <SectionShell
      id="settings"
      title="Settings"
      subtitle="Configuration that doesn't fit Status / Inputs / Processing / Data"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {cards.map(c => <CollapsibleCard key={c.id} title={c.title}>{c.render()}</CollapsibleCard>)}
      </div>
    </SectionShell>
  );
}

function CollapsibleCard({ title, children }: { title: string; children: React.ReactNode }): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '10px 14px', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{title}</span>
        <span aria-hidden="true" style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }}>▶</span>
      </button>
      {open && <div style={{ padding: '0 14px 14px 14px', borderTop: '1px solid var(--line)', paddingTop: 12 }}>{children}</div>}
    </div>
  );
}

function IdentityCard({ source, onSourceChange }: Props): React.ReactElement {
  const toast = useToast();
  const [tags, setTags] = useState(source.tags ?? {});
  const [notes, setNotes] = useState(source.notes ?? '');
  async function save() {
    try {
      const updated = await patchSource(source.source_id, { tags, notes });
      onSourceChange(updated);
      toast.show('Identity saved.', { kind: 'success' });
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 4 }}>Tags</div>
        <TagsEditor tags={tags} onChange={setTags} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 4 }}>Notes</div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={{ width: '100%', padding: '6px 8px', fontFamily: 'inherit', fontSize: 12, border: '1px solid var(--line)', borderRadius: 4, resize: 'vertical' }} />
      </div>
      <div><button className="btn sm" onClick={() => void save()}>save identity</button></div>
    </div>
  );
}

function StorageCard({ source, onSourceChange }: Props): React.ReactElement {
  const toast = useToast();
  const [retentionDays, setRetentionDays] = useState<string>(source.retention_days ? String(source.retention_days) : '');
  async function save() {
    const n = retentionDays ? Number(retentionDays) : null;
    if (retentionDays && (!Number.isInteger(n) || n! < 1 || n! > 3650)) {
      toast.show('retention_days must be an integer 1..3650 (or empty to clear).', { kind: 'error' });
      return;
    }
    try {
      const updated = await patchSource(source.source_id, { retention_days: n as number | null } as never);
      onSourceChange(updated);
      toast.show('Retention saved.', { kind: 'success' });
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12.5 }}>
      <div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>BigQuery dataset</div>
        <code style={{ fontFamily: 'var(--mono)', fontSize: 11.5, padding: '4px 8px', background: 'var(--bg-sunken)', borderRadius: 4 }}>{source.bq_dataset ?? '(per-source default)'}</code>
      </div>
      <div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>Retention (days)</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="number" min={1} max={3650} value={retentionDays} onChange={e => setRetentionDays(e.target.value)} placeholder="(no purge)" style={{ width: 100, padding: '6px 8px', fontSize: 12, border: '1px solid var(--line)', borderRadius: 4 }} />
          <button className="btn sm" onClick={() => void save()}>save</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>GCS event objects older than X days become eligible for purge via <code>POST /retention/purge</code>.</div>
      </div>
    </div>
  );
}

function SecurityCard({ source, onSourceChange }: Props): React.ReactElement {
  const toast = useToast();
  const [rotating, setRotating] = useState(false);
  const [revealed, setRevealed] = useState(false);
  async function rotate() {
    if (!window.confirm('Rotate HMAC secret?\n\nSenders signing with the OLD secret will start getting 401s immediately. Update senders FIRST if you can.')) return;
    setRotating(true);
    try {
      const r = await rotateHmacSecret(source.source_id);
      onSourceChange({ ...source, hmac_secret_ref: r.hmac_secret });
      setRevealed(true);
      toast.show('HMAC secret rotated.', { kind: 'success' });
    } catch (e) {
      toast.show(`Rotate failed: ${(e as Error).message}`, { kind: 'error' });
    } finally { setRotating(false); }
  }
  const masked = source.hmac_secret_ref ? `${source.hmac_secret_ref.slice(0, 8)}${'•'.repeat(20)}${source.hmac_secret_ref.slice(-4)}` : '—';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12.5 }}>
      <div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>HMAC secret</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ flex: 1, minWidth: 220, fontFamily: 'var(--mono)', fontSize: 11.5, padding: '6px 10px', background: 'var(--bg-sunken)', borderRadius: 4 }}>{revealed ? source.hmac_secret_ref : masked}</code>
          <button className="btn sm ghost" onClick={() => setRevealed(r => !r)}>{revealed ? 'hide' : 'reveal'}</button>
          <button className="btn sm" onClick={() => void rotate()} disabled={rotating}>{rotating ? 'rotating…' : 'rotate'}</button>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>Header & format</div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)', fontFamily: 'var(--mono)' }}>
          {source.hmac_header_name ?? 'X-Hub-Signature-256'} · {source.hmac_format ?? 'hex'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>Configure via PATCH for now (Shopify base64 / Stripe t=,v1=). Inline editor coming to v2.</div>
      </div>
      <div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Anti-replay (require timestamp)</div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{source.require_timestamp ? 'Required — bare-body signatures rejected' : 'Optional — Stripe-style timestamps verify when present'}</div>
      </div>
    </div>
  );
}

function EndpointsCard({ source }: { source: Source }): React.ReactElement {
  const toast = useToast();
  const portalUrl = source.portal_url ?? `${window.location.origin}/portal/${source.portal_token ?? ''}`;
  const bqLink = source.bq_dataset ? `https://console.cloud.google.com/bigquery?project=hub-mega-prod&ws=!1m4!1m3!3m2!1shub-mega-prod!2s${source.bq_dataset}` : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12.5 }}>
      <Row label="Slug alias" value={source.slug_alias ?? '(default — uses source_id)'} />
      <Row label="Portal token" value={source.portal_token ? `${source.portal_token.slice(0, 8)}…` : '—'} action={
        <button className="btn sm ghost" onClick={() => { void navigator.clipboard.writeText(portalUrl); toast.show('Portal URL copied.', { kind: 'success' }); }}>copy URL</button>
      } />
      {bqLink && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>BigQuery</div>
          <a href={bqLink} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)' }}>open in console →</a>
        </div>
      )}
    </div>
  );
}

function FieldAliasesCard({ source }: { source: Source }): React.ReactElement {
  const aliases = source.field_aliases ?? {};
  const entries = Object.entries(aliases);
  if (entries.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>No aliases cached. Add a <code>resolve_pipedrive_label</code> trigger to populate hash → label resolutions.</div>;
  }
  return (
    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
      <thead>
        <tr><th style={th}>Hash</th><th style={th}>Label</th><th style={th}>Resolved</th></tr>
      </thead>
      <tbody>
        {entries.slice(0, 30).map(([hash, alias]) => (
          <tr key={hash}>
            <td style={td}><code style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{hash}</code></td>
            <td style={td}>{alias.label}</td>
            <td style={td}>{alias.resolved_at ? new Date(alias.resolved_at).toLocaleDateString() : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OperationsCard({ source }: { source: Source }): React.ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12.5 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
        Workspace audit log: <a href="/api/audit?action_prefix=source." target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>open →</a>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
        Source ID: <code style={{ fontFamily: 'var(--mono)' }}>{source.source_id}</code>
      </div>
    </div>
  );
}

function DangerCard({ source }: { source: Source }): React.ReactElement {
  return (
    <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
      Archive lives in the Status section's controls (top of page) for one-click access.
      Source name: <code style={{ fontFamily: 'var(--mono)', fontSize: 11.5 }}>{source.name}</code>.
    </div>
  );
}

function Row({ label, value, action }: { label: string; value: string; action?: React.ReactNode }): React.ReactElement {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <code style={{ fontFamily: 'var(--mono)', fontSize: 11.5, padding: '4px 8px', background: 'var(--bg-sunken)', borderRadius: 4 }}>{value}</code>
        {action}
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', padding: '6px 8px', borderBottom: '1px solid var(--line)' };
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--line)', verticalAlign: 'top' };
